import { Check, X } from "lucide-react";
import { ChangeEvent, ReactNode, useMemo, useState } from "react";
import { cn } from "../../../lib/utils";
import { BaseDropdown } from "./BaseDropdown";

interface SelectDropdownProps<T> {
  value?: T | null;
  options: Array<{ name: string; value: T; display_component?: ReactNode }>;
  placeholder?: string;
  showSearch?: boolean;
  emptyMessage?: string;
  allowClear?: boolean;
  disabled?: boolean;
  wrapText?: boolean;
  buttonClassName?: string;
  onChange: (value: T | null) => void;
  className?: string;
  portalContainer?: Element;
}

export function SelectDropdown<T>({
  value,
  options,
  placeholder = "Select...",
  showSearch = true,
  emptyMessage = "No options found",
  allowClear = true,
  disabled = false,
  wrapText = false,
  buttonClassName,
  onChange,
  className,
  portalContainer,
}: SelectDropdownProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  // Filter options based on search
  const filteredOptions = useMemo(() => {
    if (!showSearch || !searchValue) return options;
    return options.filter((option) =>
      option.name.toLowerCase().includes(searchValue.toLowerCase())
    );
  }, [options, searchValue, showSearch]);

  // Get display name for selected value
  const getDisplayName = (val?: T | null) => {
    if (!val) return placeholder;
    const option = options.find((opt) => opt.value === val);
    return option ? option.name : String(val);
  };

  const handleSelect = (selectedValue: T) => {
    onChange(selectedValue);
    setIsOpen(false);
    setSearchValue("");
  };

  const handleClear = () => {
    onChange(null);
    setIsOpen(false);
  };

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  const trigger = (
    <span
      className={cn(
        "flex-1 min-w-0 text-left",
        wrapText ? "whitespace-normal break-words" : "truncate"
      )}
      title={getDisplayName(value)}
    >
      {getDisplayName(value)}
    </span>
  );

  return (
    <BaseDropdown
      trigger={trigger}
      isOpen={isOpen}
      onToggle={() => {
        if (disabled) return;
        setIsOpen(!isOpen);
        if (isOpen) setSearchValue("");
      }}
      className={className}
      portalContainer={portalContainer}
      disabled={disabled}
      buttonClassName={buttonClassName}
    >
      <div className="p-2">
        {showSearch && (
          <div className="mb-2">
            <input
              placeholder="Search..."
              className="w-full px-2 py-2 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none"
              type="text"
              value={searchValue}
              onChange={handleSearchChange}
              autoFocus
            />
          </div>
        )}

        <div className="max-h-48 overflow-y-auto">
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => (
              <button
                key={index}
                className={cn(
                  "w-full text-left px-2 py-2 rounded text-sm hover:bg-gray-100 flex items-center",
                  value === option.value && "bg-blue-50 text-blue-700"
                )}
                onClick={() => handleSelect(option.value)}
              >
                <div
                  className={cn(
                    "w-4 h-4 border rounded flex items-center justify-center mr-2 flex-shrink-0",
                    value === option.value
                      ? "bg-blue-500 border-blue-500"
                      : "border-gray-300"
                  )}
                >
                  {value === option.value && (
                    <Check className="w-3 h-3 text-white" />
                  )}
                </div>
                <span
                  className={cn(
                    "min-w-0 flex-1",
                    wrapText ? "whitespace-normal break-words" : "truncate"
                  )}
                >
                  {option.display_component || option.name}
                </span>
              </button>
            ))
          ) : (
            <div className="px-2 py-2 text-sm text-gray-500">
              {emptyMessage}
            </div>
          )}
        </div>

        {allowClear && value !== null && (
          <div className="pt-2 mt-2 border-t border-gray-200">
            <button
              className="w-full text-left px-2 py-2 text-sm text-red-500 hover:bg-red-50 rounded flex items-center"
              onClick={handleClear}
            >
              <X className="h-4 w-4 mr-2 flex-shrink-0" />
              Clear selection
            </button>
          </div>
        )}
      </div>
    </BaseDropdown>
  );
}
